import { useRef } from "react"
import { ModuleThumb, EyeThumb, FrameThumb, MiniQr } from "../QrThumbs"
import TextFields from "../TextFields"

// The five setting panels for the mobile sheet.
//
// The shape of every panel is the same and is the point of the rework: one
// plain-language lead sentence saying what the control does, then options that
// are thumbnails of their own effect. The desktop pickers label their options;
// on a phone, where the option tiles shrink to thumbnails, an unlabelled
// swatch is a guess.
//
// These are deliberately not the desktop ColorPicker / FrameSelect /
// ShapeSelect / LogoUpload components. Those are built for a wide panel with
// hover, a hex disclosure, and drag-and-drop — none of which survive the
// translation to a 44px-target sheet. What is shared is what carries meaning:
// the same option values, the same thumbnails (QrThumbs), and the same Rec. 709
// contrast test as the server's own validation. The text fields are the
// exception: two plain inputs need no translation, so TextFields is shared.

export const CATEGORIES = [
  { id: "colors", label: "Colors", lead: "The ink and the background of the code. High contrast scans fastest." },
  { id: "logo",   label: "Logo",   lead: "Drops your mark in the middle of the code. Keep it small and it still scans." },
  { id: "frame",  label: "Frame",  lead: "Adds a rounded border. Helps on busy walls and menus." },
  { id: "text",   label: "Text",   lead: "One line above or below the code: your name, or “Scan me”." },
  { id: "shape",  label: "Shape",  lead: "Restyles the dots and the corner squares. Looks only — the link is unchanged." }
]

function leadFor(id) {
  return CATEGORIES.find((c) => c.id === id).lead
}

export const COLOR_PRESETS = [
  { name: "Voltage", foreground: "#22C55E", background: "#111111" },
  { name: "Coal",    foreground: "#111111", background: "#FFFFFF" },
  { name: "Plasma",  foreground: "#7C3AED", background: "#FAFAFA" },
  { name: "Mint",    foreground: "#111111", background: "#F0FDF4" },
  { name: "Inverse", foreground: "#FFFFFF", background: "#22C55E" }
]

export const FRAMES = [
  { value: "none",    label: "None",    sub: "Bare code" },
  { value: "rounded", label: "Rounded", sub: "Border" }
]

export const MODULE_SHAPES = [
  { value: "square",  label: "Square" },
  { value: "rounded", label: "Rounded" },
  { value: "dot",     label: "Dots" },
  { value: "fluid",   label: "Fluid" }
]

export const EYE_SHAPES = [
  { value: "square",  label: "Square" },
  { value: "rounded", label: "Rounded" },
  { value: "leaf",    label: "Leaf" },
  { value: "circle",  label: "Circle" }
]

const DEFAULT_FOREGROUND = "#111111"
const DEFAULT_BACKGROUND = "#FFFFFF"

// Same Rec. 709 weights and 0.4 delta budget as the server's
// QrCode#contrast_within_budget and the desktop ColorPicker. Three
// implementations of one rule is already one too many; if this moves, move all
// three together.
function relativeLuminance(hex) {
  const m = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(hex || "")
  if (!m) return null
  let s = m[1]
  if (s.length === 3) s = s.split("").map((c) => c + c).join("")
  const r = parseInt(s.slice(0, 2), 16) / 255
  const g = parseInt(s.slice(2, 4), 16) / 255
  const b = parseInt(s.slice(4, 6), 16) / 255
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

export function contrastOK(foreground, background) {
  const lf = relativeLuminance(foreground || DEFAULT_FOREGROUND)
  const lb = relativeLuminance(background || DEFAULT_BACKGROUND)
  if (lf == null || lb == null) return null
  return Math.abs(lb - lf) >= 0.4
}

// Peek-state summaries. Derived, never stored — a stored copy is a second
// source of truth for something the design already holds.
export function summarize(design, hasLogo) {
  const fg = (design.foregroundColor || DEFAULT_FOREGROUND).toUpperCase()
  const bg = (design.backgroundColor || DEFAULT_BACKGROUND).toUpperCase()
  const preset = COLOR_PRESETS.find((p) => p.foreground.toUpperCase() === fg && p.background.toUpperCase() === bg)
  const frame = FRAMES.find((f) => f.value === (design.frameStyle || "none"))
  const shaped = (design.moduleShape || "square") !== "square" || (design.eyeShape || "square") !== "square"
  const lines = [design.headerText, design.footerText].filter((t) => (t || "").trim()).length
  return {
    colors: preset ? preset.name : "Custom",
    logo:   hasLogo ? "1 file" : "None",
    frame:  frame ? frame.label : "None",
    text:   lines === 0 ? "None" : lines === 1 ? "1 line" : "2 lines",
    shape:  shaped ? "Custom" : "Default"
  }
}

const TILE_BASE =
  "relative flex min-w-0 cursor-pointer flex-col items-center gap-1.5 rounded px-1.5 pb-2.5 pt-3 " +
  "border bg-[color:var(--bg-surface)] text-[color:var(--text-primary)] " +
  "transition-[border-color,background-color] duration-150 ease-[cubic-bezier(0.4,0,0.2,1)] " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2"

const TILE_ON  = "border-voltage bg-voltage-pale dark:bg-voltage/10 shadow-[0_0_0_1px_var(--color-voltage)]"
const TILE_OFF = "border-[color:var(--border-default)]"

function Lead({ children }) {
  return <p className="mb-3 text-sm leading-relaxed text-[color:var(--text-secondary)] text-pretty">{children}</p>
}

function LockPip() {
  return (
    <span className="absolute right-1.5 top-1.5 inline-flex text-plasma dark:text-plasma-light" aria-hidden="true">
      <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
      </svg>
    </span>
  )
}

// ── Colors ────────────────────────────────────────────────────────────────
function ColorsPanel({ design, onColors, onForeground, onBackground }) {
  const fg = design.foregroundColor || DEFAULT_FOREGROUND
  const bg = design.backgroundColor || DEFAULT_BACKGROUND
  const ok = contrastOK(fg, bg)

  return (
    <div>
      <Lead>{leadFor("colors")}</Lead>

      <div className="grid grid-cols-3 gap-2.5" role="group" aria-label="Colour combos">
        {COLOR_PRESETS.map((p) => {
          const on = p.foreground.toUpperCase() === fg.toUpperCase() && p.background.toUpperCase() === bg.toUpperCase()
          return (
            <button
              key={p.name}
              type="button"
              aria-pressed={on}
              onClick={() => onColors(p)}
              className={`${TILE_BASE} min-h-[92px] gap-2 ${on ? TILE_ON : TILE_OFF}`}
            >
              <span
                className="grid h-[46px] w-[46px] flex-shrink-0 place-items-center rounded-[10px] border border-[color:var(--border-default)]"
                style={{ background: p.background }}
              >
                <MiniQr foreground={p.foreground} background={p.background} />
              </span>
              <span className="text-sm font-semibold">{p.name}</span>
            </button>
          )
        })}

        {/* The escape hatch from the presets. A label wrapping a hidden colour
            input, so the whole 92px tile is the tap target rather than the
            browser's small native swatch. */}
        <label className={`${TILE_BASE} min-h-[92px] gap-2 ${TILE_OFF} focus-within:ring-2 focus-within:ring-voltage focus-within:ring-offset-2`}>
          <span
            className="h-[46px] w-[46px] flex-shrink-0 rounded-[10px] border border-[color:var(--border-default)]"
            style={{ background: "linear-gradient(135deg,#22C55E,#7C3AED)" }}
            aria-hidden="true"
          />
          <span className="text-sm font-semibold">Custom</span>
          <input
            type="color"
            value={fg}
            onChange={(e) => onForeground(e.target.value)}
            aria-label="Custom ink colour"
            className="absolute h-px w-px opacity-0"
          />
        </label>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2.5">
        <ColorWell label="Ink" value={fg} onChange={onForeground} />
        <ColorWell label="Background" value={bg} onChange={onBackground} />
      </div>

      <div aria-live="polite">
        {ok === false && (
          <div className="mt-3 flex items-start gap-2 rounded px-3 py-2.5 text-sm bg-warn/10 dark:bg-warn/15 text-[color:var(--text-primary)]">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 flex-shrink-0 text-warn" aria-hidden="true">
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
              <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <span>Contrast is too low — phones may struggle to scan this.</span>
          </div>
        )}
        {ok === true && (
          <div className="mt-3 flex items-start gap-2 rounded px-3 py-2.5 text-sm bg-voltage-pale dark:bg-voltage/15 text-voltage-dark dark:text-voltage-light">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 flex-shrink-0" aria-hidden="true">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>Good contrast — this should scan cleanly.</span>
          </div>
        )}
      </div>
    </div>
  )
}

function ColorWell({ label, value, onChange }) {
  return (
    <label className="flex min-h-[52px] cursor-pointer items-center gap-2.5 rounded border border-[color:var(--border-default)] px-2.5 py-1.5 focus-within:ring-2 focus-within:ring-voltage focus-within:ring-offset-2">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={`${label} colour`}
        className={[
          "h-9 w-9 flex-shrink-0 cursor-pointer appearance-none rounded border border-[color:var(--border-default)] bg-transparent p-0",
          "[&::-webkit-color-swatch-wrapper]:p-0",
          "[&::-webkit-color-swatch]:border-none",
          "[&::-webkit-color-swatch]:rounded",
          "[&::-moz-color-swatch]:border-none",
          "[&::-moz-color-swatch]:rounded"
        ].join(" ")}
      />
      <span className="flex min-w-0 flex-col">
        <b className="text-sm font-semibold">{label}</b>
        <span className="font-mono text-xs text-[color:var(--text-secondary)]">{value.toUpperCase()}</span>
      </span>
    </label>
  )
}

// ── Logo ──────────────────────────────────────────────────────────────────
const LOGO_ACCEPT = "image/png,image/jpeg,image/svg+xml"
const LOGO_MAX_BYTES = 2 * 1024 * 1024

function LogoPanel({ design, existingLogoUrl, onLogo, onRemoveLogo, error, setError }) {
  const inputRef = useRef(null)
  const file = design.logo
  const hasLogo = Boolean(file || (existingLogoUrl && !design.removeLogo))
  // QrBuilder drops the File once the logo is saved and keeps its name.
  const name = file ? file.name : (hasLogo ? (design.logoName || "Your logo") : null)

  // Same two constraints the model enforces (LOGO_CONTENT_TYPES, 2 MB), checked
  // here so the failure lands on the control rather than as a restyle 422 the
  // sheet would swallow.
  function accept(next) {
    if (!next) return
    if (!LOGO_ACCEPT.split(",").includes(next.type)) {
      setError("Logo must be PNG, JPEG, or SVG.")
      return
    }
    if (next.size > LOGO_MAX_BYTES) {
      setError("Logo is too large — keep it under 2 MB.")
      return
    }
    setError(null)
    onLogo(next)
  }

  return (
    <div>
      <Lead>{leadFor("logo")}</Lead>
      {hasLogo ? (
        <div className="flex min-h-[64px] items-center gap-3 rounded border border-[color:var(--border-default)] bg-[color:var(--bg-secondary)] px-3.5 py-3">
          <span className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-[10px] border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] font-display text-base font-bold">
            {name.slice(0, 1).toUpperCase()}
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <b className="truncate text-sm font-semibold">{name}</b>
            <span className="text-xs text-[color:var(--text-secondary)]">Centered · 18% of code</span>
          </span>
          <button
            type="button"
            aria-label="Remove logo"
            onClick={() => { if (inputRef.current) inputRef.current.value = ""; setError(null); onRemoveLogo() }}
            className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded text-[color:var(--text-secondary)] transition hover:text-[color:var(--color-fail)] focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex min-h-[64px] w-full items-center gap-3 rounded border-[1.5px] border-dashed border-[color:var(--border-default)] bg-[color:var(--bg-secondary)] px-3.5 py-3 text-left text-[color:var(--text-primary)] transition hover:border-voltage focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2"
        >
          <span className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-[10px] border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] text-[color:var(--text-secondary)]">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </span>
          <span className="min-w-0">
            <b className="block text-sm font-semibold">Upload a logo</b>
            <span className="block text-xs text-[color:var(--text-secondary)]">PNG, JPG or SVG · under 2 MB</span>
          </span>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={LOGO_ACCEPT}
        onChange={(e) => accept(e.target.files?.[0] || null)}
        className="sr-only"
      />
      {error && <p className="mt-2 text-sm text-[color:var(--color-fail)]">{error}</p>}
    </div>
  )
}

// ── Frame ─────────────────────────────────────────────────────────────────
function FramePanel({ design, onFrame }) {
  const active = design.frameStyle || "none"
  return (
    <div>
      <Lead>{leadFor("frame")}</Lead>
      <div className="grid grid-cols-2 gap-2.5" role="group" aria-label="Frame">
        {FRAMES.map((f) => {
          const on = active === f.value
          return (
            <button
              key={f.value}
              type="button"
              aria-pressed={on}
              onClick={() => onFrame(f)}
              className={`${TILE_BASE} min-h-[96px] ${on ? TILE_ON : TILE_OFF}`}
            >
              <FrameThumb frame={f.value} />
              <span className="text-sm font-semibold leading-none">{f.label}</span>
              <span className="text-center text-xs leading-tight text-[color:var(--text-secondary)]">{f.sub}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Text ───────────────────────────────────────────────────────────────────────
// The same fields as the desktop panel, at 16px so iOS doesn't zoom on focus.
function TextPanel({ design, setDesign }) {
  return (
    <div>
      <Lead>{leadFor("text")}</Lead>
      <TextFields design={design} setDesign={setDesign} idPrefix="qr-text-mobile" large />
    </div>
  )
}

// ── Shape ─────────────────────────────────────────────────────────────────
function ShapeGroup({ legend, options, value, locked, columns, Thumb, onPick, onUpgrade }) {
  return (
    <div>
      <p className="mb-2 text-xs font-bold uppercase tracking-wide text-[color:var(--text-secondary)]">{legend}</p>
      <div className={`grid gap-2.5 ${columns}`} role="group" aria-label={legend}>
        {options.map((o) => {
          const on = (value || "square") === o.value
          const isLocked = locked && o.value !== "square"
          return (
            <button
              key={o.value}
              type="button"
              aria-pressed={on}
              // Deliberately NOT aria-disabled (and not `disabled`). A locked
              // option is fully operable — it opens the Pro sheet — so marking
              // it disabled would announce "does nothing" for a control that
              // does something, and it makes the tile unclickable to anything
              // that honours the state (Playwright refuses it outright; some AT
              // skips it entirely), which would strand the upsell behind a
              // control nobody can reach. The padlock, the dimming, and the
              // "(Pro)" in the accessible name carry the meaning instead.
              aria-label={isLocked ? `${o.label} (Pro)` : undefined}
              onClick={() => (isLocked ? onUpgrade() : onPick(o))}
              className={`${TILE_BASE} min-h-[96px] ${on ? TILE_ON : TILE_OFF} ${isLocked ? "opacity-55" : ""}`}
            >
              {isLocked && <LockPip />}
              <Thumb shape={o.value} className="h-8 w-8 text-[color:var(--text-primary)]" />
              <span className="text-sm font-semibold leading-none">{o.label}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function ShapePanel({ design, locked, onModule, onEye, onUpgrade }) {
  return (
    <div>
      <Lead>{leadFor("shape")}</Lead>
      <div className="flex flex-col gap-4">
        <ShapeGroup
          legend="Dots"
          options={MODULE_SHAPES}
          value={design.moduleShape}
          locked={locked}
          columns="grid-cols-3"
          Thumb={ModuleThumb}
          onPick={onModule}
          onUpgrade={onUpgrade}
        />
        <ShapeGroup
          legend="Corners"
          options={EYE_SHAPES}
          value={design.eyeShape}
          locked={locked}
          columns="grid-cols-4"
          Thumb={EyeThumb}
          onPick={onEye}
          onUpgrade={onUpgrade}
        />
      </div>
      {locked && (
        <button
          type="button"
          onClick={onUpgrade}
          className="mt-3.5 inline-flex min-h-[44px] items-center gap-1.5 text-sm font-semibold text-plasma dark:text-plasma-light focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 rounded"
        >
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z" />
          </svg>
          Shapes are a Pro feature. Get Pro to use them.
        </button>
      )}
    </div>
  )
}

export default function MobileSettingPanels(props) {
  if (props.category === "colors") return <ColorsPanel {...props} />
  if (props.category === "logo")   return <LogoPanel {...props} />
  if (props.category === "frame")  return <FramePanel {...props} />
  if (props.category === "text")   return <TextPanel {...props} />
  return <ShapePanel {...props} />
}
